import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import {
  DynamicStatusDropdown,
  StatusOption,
} from "components/Menuitem/DynamicStatusDropdown";
import { app_modules } from "helpers/apps_modules_list";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createSelector } from "reselect";

import { Axios } from "api/axios";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { Card } from "react-bootstrap";
import {
  downloadQuotationPdf,
  exportQuotationsCSV,
  fetchQuotations,
  updateQuotationStatus,
} from "slices/quotation/thunk";
import Swal from "sweetalert2";
import { showConfirm, showCustomSwal } from "utils/swalHelper";

interface QuotationProps {
  pageIndex: number;
  pageSize: number;
  sorting: any[];
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  sortField: string;
  sortDirection: "" | "asc" | "desc" | undefined;
  setPageIndex: (value: number) => void;
  setPageSize: (value: number) => void;
  setSorting: (value: any) => void;
  setColumnFilters: (value: any) => void;
  setFilterValue: (value: any[]) => void;
  setSearch: (value: string) => void;
  setSearchInput: (value: string) => void;
  onHeaderReady?: (handlers: {
    searchInput: string;
    setSearchInput: (value: string) => void;
    handleExport: () => void;
  }) => void;
}

const Quotation: React.FC<QuotationProps> = ({
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  sortField,
  sortDirection,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setSearch,
  setSearchInput,
  onHeaderReady,
}) => {
  document.title = "Quotation List";
  const dispatch = useDispatch<any>();

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.quotation
  );

  // Redux selector for quotation state
  const selectQuotations = createSelector(
    (state: any) => state.Quotation,
    (quotation) => ({
      quotations: quotation.quotationList,
      rowCount: quotation.rowCount,
      loading: quotation.loading,
      error: quotation.error,
    })
  );

  const { quotations, rowCount, loading, error } =
    useSelector(selectQuotations);

  // Fetch quotations using thunk
  useEffect(() => {
    dispatch(
      fetchQuotations({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
  ]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  // Use data from backend (sr_no is already provided by backend)
  const data = useMemo(() => {
    return quotations;
  }, [quotations]);

  // Handle export
  const handleExport = useCallback(async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportQuotationsCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export quotation data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  }, [sortField, sortDirection, search, filterValue]);

  // Expose handlers to parent
  useEffect(() => {
    if (onHeaderReady) {
      onHeaderReady({
        searchInput,
        setSearchInput,
        handleExport,
      });
    }
  }, [searchInput, onHeaderReady, handleExport]);

  // Handle status change
  const handleStatusChange = useCallback(
    async (quotationId: string, newStatus: string) => {
      try {
        await dispatch(
          updateQuotationStatus({
            id: quotationId,
            stage: newStatus,
          })
        ).unwrap();
        toast.success(`Status updated to ${newStatus}`);
        // No need to refresh the list as it is already refreshed in the thunk after update
      } catch (err: any) {
        toast.error(err?.message || "Failed to update quotation status");
      }
    },
    [dispatch]
  );

  const handleDownloadPdf = useCallback(
    async (quotation: any) => {
      const quotationId = quotation?.quotation_id || quotation?.id;
      if (!quotationId) {
        toast.error("Quotation ID not found");
        return;
      }

      showCustomSwal({
        title: "Downloading PDF...",
        text: "Please wait while we prepare your quotation PDF.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const { blob, filename } = await dispatch(
          downloadQuotationPdf({
            id: quotationId,
            quotationNumber: quotation?.quotation_number,
          })
        ).unwrap();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.close();
        toast.success("PDF downloaded");
      } catch (error: any) {
        console.error("Quotation PDF download error:", error);
        // Extract error message from various possible error structures
        const message =
          error?.payload?.message ||
          error?.payload?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to download quotation PDF. Please contact support if the issue persists.";

        Swal.close();
        showCustomSwal({
          icon: "error",
          title: "Download Failed",
          text: message,
        });
      }
    },
    [dispatch]
  );

  const handleRequestDispatch = useCallback(
    async (quotation: any) => {
      // Show confirmation dialog before proceeding
      const result = await showConfirm(
        "Are you sure you want to request dispatch for this quotation?",
        "Request for Dispatch?",
        "Yes",
        "No"
      );

      // Only proceed if user confirmed
      if (!result.isConfirmed) {
        return;
      }

      try {
        // Show loading modal
        showCustomSwal({
          title: "Requesting Dispatch...",
          text: "Please wait while we process your request.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Call API to create order and dispatch
        await Axios.post(
          `${api?.warehouse?.dispatch?.quotation_dispatch?.create}/${quotation?.quotation_id}/create-order-and-dispatch`,
          {}
        );

        // Replace loading with success modal
        showCustomSwal({
          icon: "success",
          title: "Success",
          text: "Requested for dispatch successfully",
        });

        // Refresh the quotations list
        dispatch(
          fetchQuotations({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );

        // Navigate to dispatch form
        // navigate(`/quotation`);
      } catch (error: any) {
        console.error("Error creating order:", error);

        // Replace loading with error modal
        showCustomSwal({
          icon: "error",
          title: "Failed",
          text:
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to create order",
        });
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
    ]
  );

  // Get status color based on stage value
  const getStageColor = useCallback((stage: string) => {
    switch (stage?.toLowerCase()) {
      case "send":
        return { backgroundColor: "#87CEEB", color: "#000" }; // Light blue
      case "won":
        return { backgroundColor: "#28a745", color: "#fff" }; // Green
      case "lost":
        return { backgroundColor: "#dc3545", color: "#fff" }; // Red
      case "create dispatch":
        return { backgroundColor: "#ffc107", color: "#000" }; // Yellow
      case "pending":
        return { backgroundColor: "#6c757d", color: "#fff" }; // Gray
      default:
        return { backgroundColor: "#6c757d", color: "#fff" }; // Gray
    }
  }, []);

  // Create status options for DynamicStatusDropdown
  const statusOptions: StatusOption[] = useMemo(() => {
    return [
      {
        label: "Send",
        value: "Send",
        permission: canUpdate,
        onClick: (quotation: any, value: string) => {
          handleStatusChange(quotation.quotation_id || quotation.id, value);
        },
      },
      {
        label: "Won",
        value: "Won",
        permission: true,
        onClick: (quotation: any, value: string) => {
          handleStatusChange(quotation.quotation_id || quotation.id, value);
        },
      },
      {
        label: "Lost",
        value: "Lost",
        permission: canUpdate,
        onClick: (quotation: any, value: string) => {
          handleStatusChange(quotation.quotation_id || quotation.id, value);
        },
      },
    ];
  }, [canUpdate, handleStatusChange]);

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: (row: any) => {
          // return !!(canUpdate && row?.stage?.toLowerCase() === "pending");
          const hasOrder =
            row?.has_order || (row?.orders && row.orders.length > 0);
          return !!(canUpdate && !hasOrder);
        },
        link: (quotation: any) =>
          `/quotation/edit/${quotation.quotation_id || quotation.id}`,
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "View",
        permission: canView,
        link: (quotation: any) =>
          `/quotation/view/${quotation.quotation_id || quotation.id}`,
        color: "themeColor",
      },
      {
        icon: PictureAsPdfIcon,
        label: "Download PDF",
        permission: canView,
        onClick: handleDownloadPdf,
        color: "themeColor",
      },
      {
        icon: EmailIcon,
        label: "Send Email",
        permission: canUpdate,
        onClick: (quotation: any) => {
          // TODO: Implement send email
          toast.info("Send email will be implemented");
        },
        color: "themeColor",
      },
      {
        icon: LocalShippingIcon,
        label: "Request for dispatch",
        permission: (row: any) => {
          // Only show if quotation is "won" AND no order has been created yet
          const isWon = row?.stage?.toLowerCase() === "won";
          const hasOrder =
            row?.has_order || (row?.orders && row.orders.length > 0);
          return !!(canUpdate && isWon && !hasOrder);
        },
        onClick: handleRequestDispatch,
        color: "themeColor",
      },
    ];
    return items;
  }, [canView, canUpdate, handleDownloadPdf, handleRequestDispatch]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) {
      return [];
    }

    // Always show Actions column (as per user's comment)
    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems, canView, canUpdate]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.sr_no || "-",
      },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/quotation/dropdown/quotation_number",
        cell: ({ row }: { row: any }) => {
          return row.original.quotation_number || "-";
        },
      },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/quotation/dropdown/enquiry_number",
        cell: ({ row }: { row: any }) => {
          return row.original.enquiry?.enquiry_number || "-";
        },
      },
      {
        header: "Dispatch From Warehouse",
        accessorKey: "warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/quotation/dropdown/warehouse_name",
        cell: ({ row }: { row: any }) => {
          return row.original.warehouse?.warehouse_name || "-";
        },
      },
      {
        header: "Customer Name",
        accessorFn: (row: any) => row.customer_details?.customer_name || "",
        id: "customer_name", // Keep the ID for filtering
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/quotation/dropdown/customer_name",
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.customer_name || "-";
        },
      },
      {
        header: "Status",
        accessorKey: "stage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/quotation/dropdown/stage",
        cell: ({ row }: { row: any }) => {
          const stage = row.original.stage || "pending";
          return (
            <>
              {stage?.toLowerCase() === "won" ? (
                // Show normal chip
                // #28a745 fff
                <span
                  className="badge bg-success px-3 py-2 rounded-pill"
                  style={{ backgroundColor: "#28a745", color: "#fff" }}
                >
                  Won
                </span>
              ) : (
                // Else show dropdown component
                <DynamicStatusDropdown
                  row={row.original}
                  currentStatus={stage}
                  statusOptions={statusOptions}
                  getStatusColor={getStageColor}
                />
              )}
            </>
          );
        },
      },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const raw = row.original?.quotation_date;
          if (!raw) return "-";
          const d = new Date(raw);
          return d.toISOString().split("T")[0];
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [DynamicAction, getStageColor, statusOptions]
  );

  return (
    <Card>
      <Card.Body className="mt-3">
        <LoaderErrorWrapper loading={loading} error={error}>
          <TableContainer
            columns={columns}
            data={data}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            isGlobalFilter={false}
            SearchPlaceholder="Search Quotations..."
            manualSorting={true}
            colValue={setFilterValue}
          />
        </LoaderErrorWrapper>
      </Card.Body>
    </Card>
  );
};

export default Quotation;
